import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import { ArrowLeft, CheckCircle2, Circle, Loader2, Radar } from "lucide-react";
import { LAUNCH_DATE_ISO, LAUNCH_DATE_LABEL } from "@/components/landing/Trust";

type Status = "shipped" | "in-progress" | "planned";

const STATUS_META: Record<Status, { label: string; icon: typeof Circle; className: string }> = {
  shipped: { label: "Shipped", icon: CheckCircle2, className: "text-primary" },
  "in-progress": { label: "In progress", icon: Loader2, className: "text-accent" },
  planned: { label: "Planned", icon: Circle, className: "text-muted-foreground" },
};

const milestones: {
  when: string;
  dateTime: string;
  title: string;
  status: Status;
  items: string[];
}[] = [
  {
    when: LAUNCH_DATE_LABEL,
    dateTime: LAUNCH_DATE_ISO,
    title: "Public launch",
    status: "shipped",
    items: [
      "Daily AI-scored trend feed across 35+ sources",
      "Full launch blueprint per trend",
      "$39 lifetime licence with instant key by email",
      "Free marketplace samples — three blueprints readable without an account",
    ],
  },
  {
    when: "October 2026",
    dateTime: "2026-10",
    title: "Deeper validation",
    status: "in-progress",
    items: [
      "Competitor snapshots on every blueprint",
      "Supplier and template shortlists per trend",
      "Saved niches with acceleration alerts",
    ],
  },
  {
    when: "November 2026",
    dateTime: "2026-11",
    title: "Launch workspace",
    status: "planned",
    items: [
      "Track the trends you actually launched",
      "7-day launch plan with checkable steps",
      "Shareable blueprint links for teammates and clients",
    ],
  },
  {
    when: "Q1 2027",
    dateTime: "2027-01",
    title: "Scale & integrations",
    status: "planned",
    items: [
      "Shopify and TikTok Shop signals folded into scoring",
      "Weekly hit-rate report on past trend calls",
      "Export blueprints to Notion and PDF in one click",
    ],
  },
];

const Roadmap = () => (
  <>
    <Helmet>
      <title>Public Roadmap — Trends Discovery</title>
      <meta
        name="description"
        content="What Trends Discovery has shipped since its 6 September 2026 launch and what's coming next: validation tools, a launch workspace and new data sources."
      />
      <link rel="canonical" href="https://trendsdiscovery.com/roadmap" />
      <meta property="og:title" content="Public Roadmap — Trends Discovery" />
      <meta
        property="og:description"
        content="Milestones and timeline for everything shipping after the 2026 launch."
      />
      <meta property="og:type" content="website" />
      <meta property="og:url" content="https://trendsdiscovery.com/roadmap" />
      <meta name="twitter:card" content="summary_large_image" />
    </Helmet>

    <main className="min-h-screen bg-background text-foreground">
      <div className="container max-w-3xl py-16">
        <Link
          to="/"
          className="inline-flex items-center gap-2 font-mono text-xs text-primary hover:underline"
        >
          <ArrowLeft className="w-3 h-3" /> BACK TO HOME
        </Link>

        <div className="mt-8 flex items-center gap-2">
          <span className="grid place-items-center w-7 h-7 rounded bg-primary text-primary-foreground">
            <Radar className="w-4 h-4" />
          </span>
          <span className="font-display font-semibold">Trends.Discovery</span>
        </div>

        <h1 className="mt-6 font-display text-4xl md:text-5xl font-bold">Public roadmap</h1>
        <p className="mt-4 text-muted-foreground leading-relaxed">
          We launched on{" "}
          <time dateTime={LAUNCH_DATE_ISO} className="text-foreground font-medium">
            {LAUNCH_DATE_LABEL}
          </time>
          . Here's what's live, what we're building now, and what's queued after that.
          Your $39 lifetime licence includes all of it.
        </p>

        <div className="mt-6 flex flex-wrap gap-3">
          <Link
            to="/milestones"
            className="inline-flex items-center gap-2 rounded-full border border-border px-4 py-2 font-mono text-xs text-primary hover:bg-card"
          >
            SEE LIVE MILESTONE PROGRESS →
          </Link>
          <Link
            to="/launch"
            className="inline-flex items-center gap-2 rounded-full border border-primary/40 bg-primary/10 px-4 py-2 font-mono text-xs text-primary hover:bg-primary/20"
          >
            NEXT LAUNCH: COUNTDOWN & EARLY ACCESS →
          </Link>
        </div>

        <ol className="mt-12 relative border-l border-border pl-8 space-y-10">
          {milestones.map((m) => {
            const meta = STATUS_META[m.status];
            const Icon = meta.icon;
            return (
              <li key={m.title} className="relative">
                <span className="absolute -left-[41px] top-1 grid place-items-center w-6 h-6 rounded-full bg-background border border-border">
                  <Icon className={`w-3.5 h-3.5 ${meta.className}`} aria-hidden="true" />
                </span>
                <div className="flex flex-wrap items-center gap-3">
                  <time dateTime={m.dateTime} className="font-mono text-xs text-primary">
                    {m.when}
                  </time>
                  <span className="text-[10px] font-mono tracking-wider rounded-full border border-border px-2 py-0.5 text-muted-foreground">
                    {meta.label.toUpperCase()}
                  </span>
                </div>
                <h2 className="mt-2 font-display text-2xl font-bold">{m.title}</h2>
                <ul className="mt-3 space-y-2">
                  {m.items.map((i) => (
                    <li key={i} className="text-sm text-muted-foreground leading-relaxed">
                      · {i}
                    </li>
                  ))}
                </ul>
              </li>
            );
          })}
        </ol>

        <div className="mt-14 rounded-2xl border border-border bg-card p-6">
          <h2 className="font-display text-xl font-bold">Want something on this list?</h2>
          <p className="mt-2 text-sm text-muted-foreground leading-relaxed">
            Buyers steer the roadmap. Send requests to info@skaiboss.com or through the{" "}
            <Link to="/support" className="text-primary hover:underline">
              help page
            </Link>
            .
          </p>
          <div className="mt-5 flex flex-wrap gap-4 text-sm">
            <Link to="/marketplace" className="text-primary hover:underline">
              Browse the marketplace
            </Link>
            <Link to="/#pricing" className="text-primary hover:underline">
              Get the $39 lifetime licence
            </Link>
          </div>
        </div>
      </div>
    </main>
  </>
);

export default Roadmap;
